/**
 * CreateRMA page
 * Renders the manual RMA creation flow: order search, product selection, return details, fees, and submission.
 * Supports an `orderId` query parameter for deep-linking directly into a pre-loaded order.
 */
import {observer} from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataCardOrderOutOfPolicyIndicator from "../../components/layouts/data-card/data-card-indicators/data-card-order-out-of-policy-indicator";
import ListLayout from "../../components/layouts/list-layout/list-layout";
import useValidationGroup from "../../hooks/validation-group/validation-group-hook";
import CreateRmaResponseModal from "./components/create-rma-response-modal";
import OrderSearch from "./components/order-search";
import OrderProductListCard from "./components/order-product-list-card";
import './create-rma.scss';
import ReturnDetailsCard from "./components/return-details-card";
import RMAFeesCard from "./components/fees/rma-fees-card";
import RMAAutomationCard from "./components/rma-automation-card";
import FixedSubmitBar from "./components/fixed-submit-bar";
import SummaryCard from "./components/summary-card";
import createRmaMainStoreInstanceManager from "./stores/create-rma-main-store";
import CreateRmaModeSelector from './components/create-rma-mode-selector';
import ControlGroup from '../../components/layouts/control-group/control-group';
import DataCard from '../../components/layouts/data-card/data-card';
import FontBody1 from '../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../components/fonts/font-props';
import useDashboardTranslation from '../../dashboard-translation/hooks/dashboard-translation-hook';
import DataCardGridLayout from '../../components/layouts/data-card/data-card-layouts/data-card-grid-layout';
import FontBody3 from '../../components/fonts/font-body3/font-body3';
import LinkableList from "../../components/linkable-list/linkable-list";
import config from "../../config";
import CustomerDataCard from './components/customer-data-card';
import TranslationScope from '../../translation/translation-scope';
import { Note } from '../../components/note/note';
import IconPlatform from '../../components/icons/icon-platform/icon-platform';
import Chip, { CHIP_COLORS } from '../../components/chip/chip';
import DateTimeV2, { DATETIME_VIEW } from '../../components/datetime-v2/datetime-v2';
import { useDateTimeFormattingParams } from '../../hooks/use-datetime-formatting-params';
import ShippingOptionsCard from './components/shipping-options-card';



const CreateRMA: React.FC = observer(() => {
	const createRmaMainStore = createRmaMainStoreInstanceManager.getInstance();
	const rmaRequestStore = createRmaMainStore?.rmaRequestStore;
	const validationGroup = useValidationGroup();
	const hasInstance = createRmaMainStore !== undefined;
	const orderStore = createRmaMainStore?.orderStore;
	const { getTranslation, locale } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
	const dateTimeParams = useDateTimeFormattingParams();
	const daysSinceFulfillment = orderStore?.getDaysSinceFulfillment();
	const customer = orderStore?.customer;
	const getOrderRmaList = () => orderStore?.rmaList.map((item) => {
		return {label: item.id, href: config.apiUrl + '/dashboard/returns?rmaid=' + item.id};
	}) ?? [];

	const [searchParams] = useSearchParams();
	const orderIdParam = searchParams.get('orderId');
	const hasAutoSearched = useRef(false);

	useEffect(() => {
		if (!hasInstance) {
			createRmaMainStoreInstanceManager.loadSettings();
			createRmaMainStoreInstanceManager.createInstance();
		}
	}, [hasInstance]);

	useEffect(() => {
		if (
			!hasAutoSearched.current &&
			orderIdParam &&
			createRmaMainStore &&
			createRmaMainStore.settings.wasLoaded
		) {
			hasAutoSearched.current = true;
			createRmaMainStore.setSearchOrderName(orderIdParam);
			createRmaMainStore.searchForOrder();
		}
	}, [createRmaMainStore, orderIdParam, createRmaMainStore?.settings.wasLoaded]);

	const showTopIndicator = orderStore?.isOrderOutOfPolicy() || orderStore?.isCreateRmaNotAllowed;

	if (!createRmaMainStore) {
		return null;
	}

	return (
		<TranslationScope scope="return-reason" locale={locale}>
			<div className={'create-rma'}>
				<ListLayout>
					<div className='create-rma-header'>
						<div className="c-rma-search-line">
							<OrderSearch createRmaMainStore={createRmaMainStore}/>
							{!!orderStore && <CreateRmaModeSelector createRmaSettingsStore={createRmaMainStore.settings} />}
						</div>
						{!!!createRmaMainStore.orderStore && <CurrentLimitationsNote />}						
					</div>
					{!!orderStore &&
						<ControlGroup>
							<DataCard
                            TopIndicator={(showTopIndicator) ? DataCardOrderOutOfPolicyIndicator: undefined}
							hasTopIndicator={showTopIndicator}
							toolbar={{
								content:
								<>
									<IconPlatform orderStore={orderStore} exchangeTooltipText={getTranslation('EXCHANGE_ORDER_CREATED_BY_RETURNGO', 'dashboard-returns-rma-card')} />
									<FontBody1 color={FONT_COLOR.NEUTRAL10}>
										{getTranslation('ORDER', pageScope)} {orderStore.orderName}&nbsp;
									</FontBody1>
									{orderStore.isCovered && <Chip label={getTranslation('COVERED_BY_RETURN_GUARD', 'dashboard-returns-rma-card')} color={CHIP_COLORS.BLUE} />}

								</>
							}}
						>
							<DataCardGridLayout>
								<FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('FULFILLED', pageScope)} </FontBody3>
								<FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
									{orderStore.getFulfilledDate() &&
                                        <DateTimeV2 {...dateTimeParams} utcDateString={orderStore.getFulfilledDate()!} dateTimeView={DATETIME_VIEW.DATE_ONLY} />
                                    }
                                    {daysSinceFulfillment !== null ? ` (${daysSinceFulfillment} ${getTranslation('DAYS_AGO', pageScope)})` : ''}
								</FontBody3>
								<FontBody3 color={FONT_COLOR.PRIMARY2}>RMAs </FontBody3>
								<FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
									<LinkableList totalCount={orderStore.rmaCount} list={getOrderRmaList()}/>
								</FontBody3>
								<FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('RELATED_ORDERS', pageScope)} </FontBody3>
								<FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{orderStore.orderName}</FontBody3>
								{orderStore.location && 
									<>
										<FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('LOCATION', pageScope)}</FontBody3>
										<FontBody3 color={FONT_COLOR.PRIMARY2}variant={FONT_VARIANT.BOLD}>{orderStore.location.name}</FontBody3>
									</>
								}
							</DataCardGridLayout>
						</DataCard>

						<CustomerDataCard customer={customer!} orderShippingAddress={orderStore.shipmentCustomerAddress}
							conversionRate={orderStore.conversionRate}
							hasTopIndicator={showTopIndicator}
							currencyCode={orderStore.paymentCurrencyCode}
						/>

						</ControlGroup>
					}
					{!!createRmaMainStore.orderStore?.orderId && rmaRequestStore &&
						<>
							<OrderProductListCard createRmaSettingsStore={createRmaMainStore.settings} rmaRequestStore={rmaRequestStore} orderStore={createRmaMainStore.orderStore} />
							<ReturnDetailsCard createRmaSettingsStore={createRmaMainStore.settings} rmaRequestStore={rmaRequestStore}  createRmaMainStore={createRmaMainStore}/>
							<ShippingOptionsCard rmaRequestStore={rmaRequestStore} />
							<RMAFeesCard rmaRequestStore={rmaRequestStore} validationGroup={validationGroup} isPolicyLedMode={createRmaMainStore.settings.isPolicyLedMode}/>
							{ !createRmaMainStore.settings.isPolicyLedMode && <RMAAutomationCard validationGroup={validationGroup} rmaRequestStore={rmaRequestStore} /> }
							<SummaryCard rmaRequestStore={rmaRequestStore} />
						</>
					}
				</ListLayout>
			</div>

			{ !!createRmaMainStore.orderStore?.orderId && <FixedSubmitBar createRmaMainStore={createRmaMainStore} validtionGroup={validationGroup} /> }

            { createRmaMainStore.showResponseModal &&
              createRmaMainStore.createRmaResponseStore &&
                <CreateRmaResponseModal store={createRmaMainStore.createRmaResponseStore} createRmaMainStore={createRmaMainStore}/>
            }
		</TranslationScope>
)
});

export default CreateRMA;


const CurrentLimitationsNote = () => {
	const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';

	const body = () => {
		return (
			<div className='c-rma-search-line-note-body'>
				{getTranslation('NOTICE_NO_SUPPORT_TITLE', pageScope)}:
				<ul>
					<li>{getTranslation('NOTICE_NO_SUPPORT_ITEM1', pageScope)}</li>
					<li>{getTranslation('NOTICE_NO_SUPPORT_ITEM2', pageScope)}</li>
				</ul>
				{getTranslation('NOTICE_LIMITATIONS_TITLE', pageScope)}:
				<ul>
					<li>{getTranslation('NOTICE_LIMITATIONS_ITEM1', pageScope)}</li>
					<li>{getTranslation('NOTICE_LIMITATIONS_ITEM2', pageScope)}</li>
					<li>{getTranslation('NOTICE_LIMITATIONS_ITEM3', pageScope)}</li>
				</ul>
				<a target='_blank' rel="noreferrer" href='https://support.returngo.ai/internally-creating-rmas'>{getTranslation('NOTICE_LEARN_ABOUT_CRMA_LINK', pageScope)}</a>				
			</div>
		);
	};
	
	return (
		<Note caption={getTranslation('NOTICE_CURRENT_LIMITATIONS', pageScope)} body={body()} 
		/>

	);
}
